import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { InvitableRole } from "@/lib/api-types";

/**
 * Workspace-member management hooks (workspace-admin scope).
 *
 * All endpoints below live under /api/admin/... and are ADMIN-only server-side.
 * Non-admin users won't render the UI that calls these anyway (see
 * canInviteUsers in api-types.ts), but the backend is the authoritative gate.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/** Row returned by GET /api/admin/editors. */
export type WorkspaceMember = {
  id: string;
  name: string;
  email: string;
  role: string;                    // 'FULL_ACCESS' | 'EDIT_SHARE' | 'EDIT' (or 'ADMIN' if listed, though ADMIN is filtered app-side)
  createdAt?: string | null;
  lastLoginAt?: string | null;
  suspendedAt?: string | null;     // Migration 0016 — non-null = soft-suspended (no workspace access)
};

/** Response from POST /api/admin/editors/invite. */
export type InviteResponse = {
  ok: true;
  inviteUrl: string;
  token: string;
  role: string;
  /** Backend returns true when an existing PENDING invite was reused. */
  alreadyExists?: boolean;
};

/** Row returned by GET /api/admin/editors/invites — a PENDING invite. */
export type PendingInvite = {
  id: string;
  email: string;
  role: string | null;
  token: string;
  createdAt?: string | null;
  expiresAt?: string | null;
};

// ── Queries ──────────────────────────────────────────────────────────────────

/**
 * GET /api/admin/editors — list all workspace members.
 * Used by the Manage Members dialog to power the "is this person already a
 * member?" autocomplete. Small list, no pagination needed today.
 */
export function useMembers(enabled = true) {
  return useQuery<WorkspaceMember[]>({
    queryKey: ["admin", "members"],
    queryFn: () => api.get<WorkspaceMember[]>("/admin/editors"),
    enabled,
    staleTime: 30_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

/**
 * POST /api/admin/editors/invite — create (or reuse) an invite link.
 * Response includes the shareable inviteUrl the caller can copy or email.
 * Server returns `alreadyExists: true` when a pending invite already existed;
 * we surface that so the UI can say "existing invite reused" instead of a
 * misleading "invite sent" claim.
 */
export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation<InviteResponse, Error, { email: string; role: InvitableRole }>({
    mutationFn: (body) => api.post<InviteResponse>("/admin/editors/invite", body),
    onSuccess: () => {
      // No members list mutation from this call — the invite doesn't turn into
      // a users row until acceptance. Nothing to invalidate here.
      qc.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });
}

/**
 * POST /api/admin/editors/invite/:token/send-email — dispatch invite email.
 * Called AFTER `useInviteMember` returns the token. Kept as a separate step
 * because the copy-link flow and the email flow share the same invite row —
 * user can copy the link and never email, or email now without copying.
 */
export function useSendInviteEmail() {
  return useMutation<{ ok: true }, Error, { token: string }>({
    mutationFn: ({ token }) =>
      api.post<{ ok: true }>(`/admin/editors/invite/${token}/send-email`),
  });
}

/**
 * GET /api/admin/editors/invites — PENDING invites for the workspace.
 * Pending invites occupy a member seat, so this list is what lets an admin see
 * and revoke a mistyped invite that would otherwise lock the seat forever.
 */
export function usePendingInvites(enabled = true) {
  return useQuery<PendingInvite[]>({
    queryKey: ["admin", "invites"],
    queryFn: () => api.get<PendingInvite[]>("/admin/editors/invites"),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * DELETE /api/admin/editors/invites/:id — revoke a pending invite.
 * Frees the member seat immediately (pending invites count toward the cap).
 * Invalidates both the invite list and the members list (seat count changes).
 */
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) => api.delete<{ ok: true }>(`/admin/editors/invites/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "invites"] });
      qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}

/**
 * DELETE /api/admin/editors/:id — remove a workspace member.
 * Backend rejects removal of ADMIN (workspace owner) and returns 400 for self —
 * frontend hides the button in those cases as a first line of defense.
 * On success, invalidates the members list.
 */
export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) => api.delete<{ ok: true }>(`/admin/editors/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}

/**
 * DELETE /api/admin/editors/:id/permanent — HARD delete a workspace member.
 * Purges the user row for good (soft-suspend just hides + blocks login). Their
 * uploads/comments stay but lose author identity. Re-adding is invite-only.
 * On success, invalidates the members list.
 */
export function useDeleteMemberPermanent() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) => api.delete<{ ok: true }>(`/admin/editors/${id}/permanent`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}

/**
 * POST /api/admin/editors/:id/restore — undo a soft-suspend (migration 0016).
 * Clears `suspendedAt`, giving the member workspace access back. No-op if the
 * member wasn't suspended. On success, invalidates the members list.
 */
export function useRestoreMember() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) => api.post<{ ok: true }>(`/admin/editors/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}

/**
 * PATCH /api/admin/editors/:id/role — change an existing member's role.
 * Backend rejects role changes on the workspace ADMIN (only one per workspace).
 * On success, invalidates the members list so any open UI picks up the new role.
 */
export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; id: string; role: string },
    Error,
    { id: string; role: InvitableRole }
  >({
    mutationFn: ({ id, role }) =>
      api.patch<{ ok: true; id: string; role: string }>(`/admin/editors/${id}/role`, { role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}
